import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  HomeIcon, UserIcon, GlobeAmericasIcon, BoltIcon, UserGroupIcon, ChartBarIcon, StarIcon,
  CommandLineIcon, ShoppingBagIcon, ScaleIcon, BookOpenIcon, AcademicCapIcon, ShieldCheckIcon,
  QuestionMarkCircleIcon, ChevronDownIcon, ChevronUpIcon
} from '@heroicons/react/24/solid';
import {
  HomeIcon as HomeOutline, UserIcon as UserOutline, GlobeAmericasIcon as GlobeOutline,
  BoltIcon as BoltOutline, UserGroupIcon as UserGroupOutline,
  ChartBarIcon as ChartBarOutline,
  ArrowRightOnRectangleIcon, StarIcon as StarOutline, XMarkIcon,
  CommandLineIcon as CommandLineOutline, ShoppingBagIcon as ShoppingBagOutline,
  Cog6ToothIcon, BellIcon, BookOpenIcon as BookOutline,
  ChatBubbleLeftRightIcon, HandThumbUpIcon, AtSymbolIcon, GiftIcon, UserPlusIcon,
  MegaphoneIcon, DocumentTextIcon, ShieldExclamationIcon, TrashIcon, RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks';
import { doc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Capacitor } from '@capacitor/core'; // Platform Check
import { deleteUserAccount } from '../api'; // Account Deletion

const NavItem = ({ to, label, IconOutline, IconSolid, end = false, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 group mx-2 ${isActive
        ? 'bg-slate-100 text-slate-900 font-semibold'
        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
      }`
    }
  >
    {({ isActive }) => (
      <>
        {isActive ? <IconSolid className="w-5 h-5" /> : <IconOutline className="w-5 h-5" />}
        <span className="font-medium text-sm">{label}</span>
      </>
    )}
  </NavLink>
);

// --- HELPER: Rank Calculation & Styling ---
const XP_DIFFICULTY_MODIFIER = 250;

const getRankStyles = (rankName) => {
  const styles = {
    'Iron': 'bg-neutral-600 text-neutral-100 border-neutral-500',
    'Bronze': 'bg-orange-800 text-orange-100 border-orange-700',
    'Silver': 'bg-slate-300 text-slate-800 border-slate-400',
    'Gold': 'bg-yellow-400 text-yellow-900 border-yellow-500',
    'Platinum': 'bg-cyan-100 text-cyan-900 border-cyan-300',
    'Emerald': 'bg-emerald-500 text-white border-emerald-600',
    'Diamond': 'bg-blue-600 text-white border-blue-700',
    'Master': 'bg-purple-700 text-white border-purple-800',
    'Grandmaster': 'bg-red-700 text-white border-red-800',
    'Challenger': 'bg-indigo-900 text-white border-indigo-700',
    'Legend': 'bg-black text-yellow-400 border-yellow-500 shadow-xl shadow-yellow-500/20',
  };
  return styles[rankName] || 'bg-slate-900 text-white';
};

const getRankInfo = (level) => {
  const RANKS = [
    'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Emerald', 
    'Diamond', 'Master', 'Grandmaster', 'Challenger', 'Legend'
  ];
  const TIERS = ['I', 'II', 'III'];
  
  const safeLevel = Math.max(1, level);
  const rankIndex = Math.floor((safeLevel - 1) / 3);
  const tierIndex = (safeLevel - 1) % 3;

  if (rankIndex >= RANKS.length) {
      return { name: 'Legend', label: 'Legend', style: getRankStyles('Legend') };
  }

  const rankName = RANKS[rankIndex];
  return {
    name: rankName,
    label: `${rankName} ${TIERS[tierIndex]}`,
    style: getRankStyles(rankName)
  };
};

// --- MANUAL CONTENT ---
const MANUAL_SECTIONS = [
    {
        id: 'profile',
        title: 'Identity & Barracks',
        icon: UserIcon,
        content: `
            <p class="mb-2"><strong class="text-slate-900">Editing Profile:</strong> Navigate to your Profile > 'Edit Barracks'. Here you can update your Callsign (handle), Bio, and Base of Operations (City).</p>
            <p class="mb-2"><strong class="text-slate-900">Avatar System:</strong> Your visual identity is generated from a 'Seed'. Type any word in the avatar field to generate a unique face.</p>
            <p><strong class="text-slate-900">Rank & Prestige:</strong> Your Level is determined by total XP. Your Rank (Iron I to Legend) showcases your skill tier. Earn XP by debating, winning matches, and receiving reactions.</p>
        `
    },
    {
        id: 'combat',
        title: 'Combat Protocols',
        icon: BoltIcon,
        content: `
            <p class="mb-2"><strong class="text-slate-900">Open Debates:</strong> Asynchronous, free-for-all arguments on global topics. Post anytime.</p>
            <p class="mb-2"><strong class="text-slate-900">The Arena:</strong> Structured 1v1 or Team battles. 
            <br/>• <strong>Classic:</strong> 24-hour turn timer. Strategic and researched.
            <br/>• <strong>Blitz:</strong> 5-minute turn timer. Fast-paced intellectual brawl.</p>
            <p><strong class="text-slate-900">Scoring:</strong> The AI Adjudicator grades your Logic, Evidence, and Civility. Community votes (Impact) also contribute to your final score.</p>
        `
    },
    {
        id: 'assets',
        title: 'Tactical Assets',
        icon: ShoppingBagIcon,
        content: `
            <p class="mb-2"><strong class="text-slate-900">Logic Bomb (Gavel):</strong> Use to highlight a brilliantly reasoned point. Increases author's Rep.</p>
            <p class="mb-2"><strong class="text-slate-900">Evidence Bomb (Book):</strong> Use to validate a well-sourced claim. Increases author's Rep.</p>
            <p class="mb-2"><strong class="text-slate-900">Insight Bomb (Cap):</strong> The highest honor. Massive Rep boost for the author.</p>
            <p><strong class="text-slate-900">XP Boost:</strong> Consumable. Doubles all XP earned for 24 hours.</p>
        `
    },
    {
        id: 'houses',
        title: 'Faction Operations',
        icon: UserGroupIcon,
        content: `
            <p class="mb-2"><strong class="text-slate-900">Joining:</strong> Find a House in the 'Groups' tab to align with. Houses compete for territory.</p>
            <p class="mb-2"><strong class="text-slate-900">Bounties:</strong> Weekly cooperative goals (e.g., "Post 50 Logical Arguments"). Targets scale based on house size.</p>
            <p><strong class="text-slate-900">House Chest:</strong> Unlocks on Sunday if bounties are met. Contains randomized loot (Bombs, XP) for all contributing members.</p>
        `
    },
    {
        id: 'conquest',
        title: 'Global Conquest',
        icon: GlobeAmericasIcon,
        content: `
            <p class="mb-2"><strong class="text-slate-900">Territory Control:</strong> Post arguments in 'Hotzones' (Red regions on the Map) to capture them for your House.</p>
            <p><strong class="text-slate-900">Imperial Tribute:</strong> If your House holds a region at 12:00 daily, every member receives a Tribute (Rep + Chance of Items).</p>
        `
    }
];

// --- SETTINGS MODAL ---
const SettingsModal = ({ onClose, user }) => {
    const [activeTab, setActiveTab] = useState('manual');
    const [expandedSection, setExpandedSection] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Initialize state from user preferences (defaulting to true if undefined)
    const [prefs, setPrefs] = useState({
        notificationsEnabled: user?.preferences?.notificationsEnabled !== false,
        notifyReplies: user?.preferences?.notifyReplies !== false,
        notifyUpvotes: user?.preferences?.notifyUpvotes !== false,
        notifyMentions: user?.preferences?.notifyMentions !== false,
        notifyAwards: user?.preferences?.notifyAwards !== false,
        notifyFollowing: user?.preferences?.notifyFollowing !== false,
        notifyFollowers: user?.preferences?.notifyFollowers !== false,
        notifyGameTurns: user?.preferences?.notifyGameTurns !== false,
    });

    const updatePreference = async (key, value) => {
        setPrefs(prev => ({ ...prev, [key]: value })); // Optimistic update
        try {
            await updateDoc(doc(db, 'users', user.uid), {
                [`preferences.${key}`]: value
            });
        } catch (e) {
            console.error("Error updating preference:", e);
            setPrefs(prev => ({ ...prev, [key]: !value })); // Revert on error
        }
    };

    const toggleSection = (id) => {
        setExpandedSection(expandedSection === id ? null : id);
    };

    // --- COMPLIANCE: DELETE ACCOUNT HANDLER ---
    const handleDeleteAccount = async () => {
        if (window.confirm("DANGER: This will permanently delete your account, data, and profile. This cannot be undone. Are you sure?")) {
            setDeleting(true);
            try {
                await deleteUserAccount();
                window.location.reload(); // Force reload to clear state and return to login
            } catch (e) {
                alert("Deletion failed (Requires recent login): " + e.message);
                setDeleting(false);
            }
        }
    };

    // --- COMPLIANCE: PLATFORM LINKS ---
    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';
    
    const privacyLink = "https://verbi-bd770.web.app/privacy_policy.html"; // Same for both
    const termsLink = isIOS 
        ? "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" // Apple Standard EULA
        : "https://verbi-bd770.web.app/terms.html"; // Custom Terms

    // Helper for toggle switch
    const ToggleSwitch = ({ label, description, checked, onChange, icon: Icon }) => (
        <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
            <div className="flex items-start pr-4">
                <div className={`p-2 rounded-lg mr-3 mt-0.5 ${checked ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <h4 className={`text-sm font-bold ${checked ? 'text-slate-900' : 'text-slate-500'}`}>{label}</h4>
                    <p className="text-xs text-slate-400 font-medium leading-tight">{description}</p>
                </div>
            </div>
            <button 
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-slate-900' : 'bg-slate-300'}`}
            >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col max-h-[80vh]">
                
                {/* Header */}
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white flex-shrink-0">
                    <h3 className="font-black text-sm uppercase tracking-wider flex items-center">
                        <Cog6ToothIcon className="w-5 h-5 mr-2 text-yellow-500" /> System Control
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-full transition-colors"><XMarkIcon className="w-5 h-5 text-slate-400 hover:text-white" /></button>
                </div>
                
                {/* Tabs */}
                <div className="flex border-b border-slate-200 bg-slate-50 flex-shrink-0">
                    <button 
                        onClick={() => setActiveTab('manual')}
                        className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'manual' ? 'bg-white text-slate-900 border-b-2 border-yellow-500' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Field Manual
                    </button>
                    <button 
                        onClick={() => setActiveTab('system')}
                        className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'system' ? 'bg-white text-slate-900 border-b-2 border-yellow-500' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Settings
                    </button>
                </div>
                
                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-white p-0">
                    {activeTab === 'system' ? (
                        <div className="p-6 space-y-6">
                            
                            {/* MASTER SWITCH */}
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                        <BellIcon className={`w-5 h-5 mr-2 ${prefs.notificationsEnabled ? 'text-slate-900' : 'text-slate-400'}`} />
                                        <span className="font-black text-sm uppercase tracking-wider text-slate-900">Push Notifications</span>
                                    </div>
                                    <button 
                                        onClick={() => updatePreference('notificationsEnabled', !prefs.notificationsEnabled)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prefs.notificationsEnabled ? 'bg-green-500' : 'bg-slate-300'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${prefs.notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500 font-medium">Master control. Turn off to silence all signals.</p>
                            </div>

                            {/* GRANULAR SETTINGS */}
                            {prefs.notificationsEnabled && (
                                <div className="animate-fade-in space-y-1">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Notification Types</h4>
                                    
                                    <ToggleSwitch 
                                        label="New Replies" 
                                        description="When someone responds to your argument."
                                        icon={ChatBubbleLeftRightIcon}
                                        checked={prefs.notifyReplies}
                                        onChange={(val) => updatePreference('notifyReplies', val)}
                                    />
                                    
                                    <ToggleSwitch 
                                        label="Mentions & Discussions" 
                                        description="When you are tagged (@) or discussed."
                                        icon={AtSymbolIcon}
                                        checked={prefs.notifyMentions}
                                        onChange={(val) => updatePreference('notifyMentions', val)}
                                    />

                                    <ToggleSwitch 
                                        label="Upvotes (Likes)" 
                                        description="When someone upvotes your post."
                                        icon={HandThumbUpIcon}
                                        checked={prefs.notifyUpvotes}
                                        onChange={(val) => updatePreference('notifyUpvotes', val)}
                                    />

                                    <ToggleSwitch 
                                        label="Gifts & Awards" 
                                        description="Insight Bombs and Tributes."
                                        icon={GiftIcon}
                                        checked={prefs.notifyAwards}
                                        onChange={(val) => updatePreference('notifyAwards', val)}
                                    />

                                    <ToggleSwitch 
                                        label="Following Activity" 
                                        description="When someone you follow posts a new argument."
                                        icon={MegaphoneIcon}
                                        checked={prefs.notifyFollowing}
                                        onChange={(val) => updatePreference('notifyFollowing', val)}
                                    />

                                    <ToggleSwitch 
                                        label="New Followers" 
                                        description="When someone follows you."
                                        icon={UserPlusIcon}
                                        checked={prefs.notifyFollowers}
                                        onChange={(val) => updatePreference('notifyFollowers', val)}
                                    />

                                    <ToggleSwitch 
                                        label="Game Turns" 
                                        description="Alerts for Arena turns and timeouts."
                                        icon={BoltOutline}
                                        checked={prefs.notifyGameTurns}
                                        onChange={(val) => updatePreference('notifyGameTurns', val)}
                                    />
                                </div>
                            )}

                            {/* LEGAL & COMPLIANCE (REQUIRED FOR APPLE) */}
                            <div className="pt-6 border-t border-slate-100">
                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 ml-1">Legal & Compliance</h4>
                                <div className="space-y-3">
                                    <a 
                                        href={privacyLink}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="flex items-center">
                                            <ShieldExclamationIcon className="w-5 h-5 mr-3 text-slate-500" />
                                            <span className="text-sm font-bold text-slate-700">Privacy Policy</span>
                                        </div>
                                        <ArrowRightOnRectangleIcon className="w-4 h-4 text-slate-400 rotate-[-45deg]" />
                                    </a>
                                    
                                    <a 
                                        href={termsLink}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="flex items-center">
                                            <DocumentTextIcon className="w-5 h-5 mr-3 text-slate-500" />
                                            <span className="text-sm font-bold text-slate-700">Terms of Service (EULA)</span>
                                        </div>
                                        <ArrowRightOnRectangleIcon className="w-4 h-4 text-slate-400 rotate-[-45deg]" />
                                    </a>
                                    
                                    {/* DELETE ACCOUNT BUTTON */}
                                    <button 
                                        onClick={handleDeleteAccount}
                                        disabled={deleting}
                                        className="w-full flex items-center justify-center p-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-100 mt-4"
                                    >
                                        {deleting ? (
                                            <span className="text-sm font-bold animate-pulse">Deleting Account...</span>
                                        ) : (
                                            <>
                                                <TrashIcon className="w-5 h-5 mr-2" />
                                                <span className="text-sm font-bold">Delete Account</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {MANUAL_SECTIONS.map((section) => {
                                const isOpen = expandedSection === section.id;
                                return (
                                    <div key={section.id} className="bg-white">
                                        <button 
                                            onClick={() => toggleSection(section.id)}
                                            className={`w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors ${isOpen ? 'bg-slate-50' : ''}`}
                                        >
                                            <div className="flex items-center">
                                                <section.icon className={`w-5 h-5 mr-3 ${isOpen ? 'text-yellow-500' : 'text-slate-400'}`} />
                                                <span className={`text-sm font-bold uppercase tracking-wide ${isOpen ? 'text-slate-900' : 'text-slate-600'}`}>
                                                    {section.title}
                                                </span>
                                            </div>
                                            {isOpen ? <ChevronUpIcon className="w-4 h-4 text-slate-400" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400" />}
                                        </button>
                                        {isOpen && (
                                            <div 
                                                className="px-12 pb-6 text-xs leading-relaxed text-slate-600 animate-fade-in"
                                                dangerouslySetInnerHTML={{ __html: section.content }} 
                                            />
                                        )}
                                    </div>
                                );
                            })}
                             <div className="p-8 text-center bg-slate-50">
                                <p className="text-[10px] text-slate-400 font-mono uppercase">Verbi Operations Manual v1.0</p>
                             </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-100 text-center flex-shrink-0">
                    <button onClick={onClose} className="text-xs font-bold text-slate-500 hover:text-slate-900 uppercase tracking-wider">
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
};

function SideNavBar({ isOpen, onClose, onOpenAdmin, onOpenModeration }) {
  const { currentUser, logout, isAdmin } = useAuth();
  const [boostTimeLeft, setBoostTimeLeft] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // Dynamic XP Bar Logic
  let levelProgress = 0;
  let currentLevel = 1;
  let rankInfo = { label: 'Unranked', style: 'bg-slate-200 text-slate-500' };

  if (currentUser?.stats) {
    const currentXp = currentUser.stats.xp || 0;
    currentLevel = Math.max(1, Math.floor(Math.sqrt(currentXp / XP_DIFFICULTY_MODIFIER)));

    const xpForNextLevel = Math.pow(currentLevel + 1, 2) * XP_DIFFICULTY_MODIFIER;
    const xpForCurrentLevel = Math.pow(currentLevel, 2) * XP_DIFFICULTY_MODIFIER;
    
    const progress = ((currentXp - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)) * 100 || 0;
    levelProgress = Math.min(100, Math.max(5, progress));
    
    rankInfo = getRankInfo(currentLevel);
  }

  // Inventory Counts
  const inventory = currentUser?.inventory || {};
  const logicCount = inventory.logic_bomb || 0;
  const evidenceCount = inventory.evidence_bomb || 0;
  const insightCount = inventory.insight_bomb || 0;
  const xpBoostCount = inventory.xp_boost_24h || 0;
  const repTotal = currentUser?.stats?.repTotal || 0; // NEW: Get Rep

  // --- BOOST TIMER LOGIC ---
  useEffect(() => {
    if (currentUser?.boostExpiresAt) {
      const updateTimer = () => {
        const now = new Date();
        const expires = currentUser.boostExpiresAt.toDate();
        const diff = expires - now;
        
        if (diff <= 0) {
          setBoostTimeLeft(null);
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setBoostTimeLeft(`${hours}h ${mins}m`);
        }
      };

      updateTimer(); // Run immediately
      const interval = setInterval(updateTimer, 60000); // Update every minute
      return () => clearInterval(interval);
    } else {
      setBoostTimeLeft(null);
    }
  }, [currentUser?.boostExpiresAt]);

  const handleActivateBoost = async () => {
    if (boostTimeLeft) return; // Already active
    if (xpBoostCount <= 0) {
      alert("No XP Boosts available. Buy one in the Store!");
      return;
    }

    if (window.confirm("Activate 2x XP Boost for 24 hours?")) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const now = new Date();
        const expires = new Date(now.getTime() + (24 * 60 * 60 * 1000));

        await updateDoc(userRef, {
          'inventory.xp_boost_24h': increment(-1),
          'boostExpiresAt': Timestamp.fromDate(expires)
        });
        alert("Boost Active! Earn double XP on all posts.");
      } catch (e) {
        console.error("Activation failed:", e);
        alert("Failed to activate boost.");
      }
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes metal-sheen {
            0% { background-position: 200% center; }
            100% { background-position: -200% center; }
          }
          .metal-icon {
            background: linear-gradient(
              135deg, 
              var(--metal-dark) 0%, 
              var(--metal-mid) 25%, 
              var(--metal-light) 40%, 
              #ffffff 50%, 
              var(--metal-light) 60%, 
              var(--metal-mid) 75%, 
              var(--metal-dark) 100%
            );
            background-size: 250% auto;
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: metal-sheen 4s linear infinite;
            filter: drop-shadow(0 1px 1px rgba(0,0,0,0.2));
          }
          /* GOLD (Logic) */
          .metal-gold {
            --metal-dark: #854d0e;
            --metal-mid: #ca8a04;
            --metal-light: #fde047;
          }
          /* COBALT/STEEL (Evidence) */
          .metal-cobalt {
            --metal-dark: #1e3a8a;
            --metal-mid: #2563eb;
            --metal-light: #93c5fd;
          }
          /* AMETHYST (Insight) */
          .metal-amethyst {
            --metal-dark: #581c87;
            --metal-mid: #9333ea;
            --metal-light: #e9d5ff;
          }
          /* BRONZE (XP Boost) */
          .metal-bronze {
            --metal-dark: #451a03;
            --metal-mid: #92400e;
            --metal-light: #fdba74;
          }
        `}
      </style>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} user={currentUser} />}

      {/* Mobile/Tablet Backdrop */}
      <div
        className={`laptop:hidden fixed inset-0 bg-slate-900/50 z-40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Sidebar / Drawer */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-white border-r border-slate-100 z-[60] flex flex-col
        transition-transform duration-300 ease-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        laptop:translate-x-0
      `}
      style={{ 
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)' // ADDED: Push content up from system nav
      }}
      >
        <div className="px-6 py-6 md:py-8 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2" onClick={onClose}>
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-black text-xl">V</span>
            </div>
            <h1 className="text-xl font-black tracking-tight text-slate-900">VERBI</h1>
          </Link>
          <button onClick={onClose} className="laptop:hidden p-1 text-slate-400 hover:text-slate-900 transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {currentUser && (
          <div className="mx-4 mb-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Profile Top Section */}
            <div className="p-4 pb-0">
              <div className="flex items-center space-x-3 mb-3">
                <img src={currentUser.photoURL} alt="Profile" className="w-12 h-12 rounded-full bg-slate-100 border-2 border-white shadow-md object-cover" />
                <div>
                  <div className="font-black text-base text-slate-900 leading-tight flex items-center">
                    {currentUser.handle || currentUser.displayName}
                    {currentUser.foundersBadge && (
                      <span className="ml-1.5 inline-flex items-center justify-center bg-cyan-100 text-cyan-700 rounded p-0.5 shadow-sm" title="Founding Member">
                        <RocketLaunchIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  {currentUser.purchasedTitle && <div className="text-[10px] text-yellow-600 font-bold uppercase tracking-wide italic mb-1">{currentUser.purchasedTitle}</div>}

                  <div className="flex items-center gap-2 mt-1">
                    <span className="bg-slate-900 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase">LVL {currentLevel}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${rankInfo.style}`}>
                      {rankInfo.label}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* XP Bar */}
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div
                  className={`h-full transition-all duration-500 ease-out rounded-full ${boostTimeLeft ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`}
                  style={{ width: `${levelProgress}%` }}
                ></div>
              </div>
            </div>

            {/* Inventory / Stats Grid - WITH METAL SHINE EFFECT */}
            <div className="grid grid-cols-5 border-t border-slate-100 bg-slate-50/50">
                <div className="flex flex-col items-center justify-center py-3 border-r border-slate-100" title="Reputation Points">
                    <ShieldCheckIcon className="w-5 h-5 text-blue-600 mb-1" />
                    <span className="text-xs font-black text-slate-900">{repTotal}</span>
                </div>
                <div className="flex flex-col items-center justify-center py-3 border-r border-slate-100" title="Logic Bombs (Gold)">
                    <ScaleIcon className="w-6 h-6 mb-1 metal-icon metal-gold" />
                    <span className="text-xs font-black text-slate-900">{logicCount}</span>
                </div>
                <div className="flex flex-col items-center justify-center py-3 border-r border-slate-100" title="Evidence Bombs (Cobalt)">
                    <BookOpenIcon className="w-6 h-6 mb-1 metal-icon metal-cobalt" />
                    <span className="text-xs font-black text-slate-900">{evidenceCount}</span>
                </div>
                <div className="flex flex-col items-center justify-center py-3 border-r border-slate-100" title="Insight Bombs (Amethyst)">
                    <AcademicCapIcon className="w-6 h-6 mb-1 metal-icon metal-amethyst" />
                    <span className="text-xs font-black text-slate-900">{insightCount}</span>
                </div>
                
                {/* XP BOOST BUTTON */}
                <button 
                    onClick={handleActivateBoost}
                    disabled={!boostTimeLeft && xpBoostCount <= 0}
                    className={`flex flex-col items-center justify-center py-3 transition-colors ${boostTimeLeft ? 'bg-green-50 cursor-default' : (xpBoostCount > 0 ? 'hover:bg-green-50 cursor-pointer' : 'opacity-50 cursor-not-allowed')}`} 
                    title={boostTimeLeft ? `Active: ${boostTimeLeft} remaining` : "Activate 2x XP Boost"}
                >
                    {boostTimeLeft ? (
                        <BoltIcon className="w-5 h-5 mb-1 text-green-600 animate-bounce" />
                    ) : (
                        <BoltIcon className="w-6 h-6 mb-1 metal-icon metal-bronze" />
                    )}
                    <span className={`text-xs font-black ${boostTimeLeft ? 'text-green-700' : 'text-slate-900'}`}>
                        {boostTimeLeft ? boostTimeLeft : xpBoostCount}
                    </span>
                </button>
            </div>
          </div>
        )}

        <nav className="flex-grow space-y-1 overflow-y-auto">
          <NavItem to="/" label="Lobby" IconOutline={HomeOutline} IconSolid={HomeIcon} end={true} onClick={onClose} />
          <NavItem to="/pantheon" label="The Pantheon" IconOutline={StarOutline} IconSolid={StarIcon} onClick={onClose} />
          <NavItem to="/communities" label="Groups" IconOutline={UserGroupOutline} IconSolid={UserGroupIcon} onClick={onClose} />
          <NavItem to="/map" label="Global Ops" IconOutline={GlobeOutline} IconSolid={GlobeAmericasIcon} onClick={onClose} />

          <NavItem to="/store" label="Marketplace" IconOutline={ShoppingBagOutline} IconSolid={ShoppingBagIcon} onClick={onClose} />

          {currentUser && (
            <NavItem to="/analytics" label="Intel" IconOutline={ChartBarOutline} IconSolid={ChartBarIcon} onClick={onClose} />
          )}

          {currentUser && (
            <NavItem to={`/profile/${currentUser.uid}`} label="Barracks" IconOutline={UserOutline} IconSolid={UserIcon} onClick={onClose} />
          )}
        </nav>

        <div className="p-4 border-t border-slate-100">
          {isAdmin && (
            <>
              <button
                onClick={() => { onOpenAdmin(); onClose(); }}
                className="flex items-center space-x-3 px-4 py-2.5 w-full text-slate-900 bg-slate-50 border border-slate-200 hover:bg-slate-900 hover:text-white hover:border-slate-900 rounded-lg transition-all mb-2 group"
              >
                <CommandLineOutline className="w-5 h-5 text-slate-500 group-hover:text-yellow-500 transition-colors" />
                <span className="font-bold text-sm tracking-wide">Admin Console</span>
              </button>
              <button
                onClick={() => { onOpenModeration(); onClose(); }}
                className="flex items-center space-x-3 px-4 py-2.5 w-full text-red-700 bg-red-50 border border-red-200 hover:bg-red-700 hover:text-white hover:border-red-700 rounded-lg transition-all mb-2 group"
              >
                <ShieldExclamationIcon className="w-5 h-5 text-red-500 group-hover:text-white transition-colors" />
                <span className="font-bold text-sm tracking-wide">Content Moderation</span>
              </button>
            </>
          )}

          {/* NEW: SETTINGS BUTTON */}
          <button
            onClick={() => { setShowSettings(true); onClose(); }}
            className="flex items-center space-x-3 px-4 py-2 w-full text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all"
          >
            <Cog6ToothIcon className="w-5 h-5" />
            <span className="font-medium text-sm">Settings</span>
          </button>

          <Link
            to="/contact"
            onClick={onClose}
            className="flex items-center space-x-3 px-4 py-2 w-full text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all"
          >
            <ShieldExclamationIcon className="w-5 h-5" />
            <span className="font-medium text-sm">Contact & Report</span>
          </Link>

          <button
            onClick={() => { logout(); onClose(); }}
            className="flex items-center space-x-3 px-4 py-2 w-full text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span className="font-medium text-sm">Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default SideNavBar;